import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ASSISTANT_API } from '@starjack/utils';
import type {
    AssistantConfigPayload,
    AssistantHealth,
    ChatDetail,
    ChatListPage,
    PageKnowledgeEntry,
    PredefinedQuestionRecord,
    ProductTextPayload,
} from './types';

/**
 * Its own api rather than an `injectEndpoints` on `baseApiAuthenticated`: the
 * assistant is a separate service on a separate host, so it needs a different
 * `baseUrl`, and RTK Query binds `baseUrl` per api.
 */
export const assistantApi = createApi({
    reducerPath: 'assistantApi',
    tagTypes: ['Config', 'Pages', 'Questions', 'Chats', 'ProductTexts'],
    baseQuery: fetchBaseQuery({
        baseUrl: `${ASSISTANT_API}/admin/assistant/`,
        prepareHeaders: (headers, { getState }) => {
            const token = (getState() as { authSlice?: { jwt?: string } })
                .authSlice?.jwt;
            if (token) headers.set('Authorization', `bearer ${token}`);
            return headers;
        },
    }),
    endpoints: (builder) => ({
        getAssistantHealth: builder.query<AssistantHealth, void>({
            query: () => 'health',
        }),
        getAssistantConfig: builder.query<AssistantConfigPayload, void>({
            query: () => 'config',
            providesTags: ['Config'],
        }),
        putAssistantConfig: builder.mutation<
            AssistantConfigPayload,
            AssistantConfigPayload
        >({
            query: (body) => ({ url: 'config', method: 'PUT', body }),
            invalidatesTags: ['Config'],
        }),
        getAssistantPages: builder.query<PageKnowledgeEntry[], void>({
            query: () => 'pages',
            providesTags: ['Pages'],
        }),
        putAssistantPage: builder.mutation<
            { ok: true },
            { slug: string; text: string }
        >({
            query: ({ slug, text }) => ({
                url: `pages/${encodeURIComponent(slug)}`,
                method: 'PUT',
                body: { text },
            }),
            invalidatesTags: ['Pages'],
        }),
        getAssistantQuestions: builder.query<PredefinedQuestionRecord[], void>({
            query: () => 'questions',
            providesTags: ['Questions'],
        }),
        // `updatedBy` is never sent — the server derives it from the token.
        createAssistantQuestion: builder.mutation<
            PredefinedQuestionRecord,
            Omit<PredefinedQuestionRecord, 'id'>
        >({
            query: (body) => ({ url: 'questions', method: 'POST', body }),
            invalidatesTags: ['Questions'],
        }),
        updateAssistantQuestion: builder.mutation<
            PredefinedQuestionRecord,
            { id: string } & Omit<PredefinedQuestionRecord, 'id'>
        >({
            query: ({ id, ...body }) => ({
                url: `questions/${encodeURIComponent(id)}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Questions'],
        }),
        deleteAssistantQuestion: builder.mutation<{ ok: true }, string>({
            query: (id) => ({
                url: `questions/${encodeURIComponent(id)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Questions'],
        }),
        // Offset pagination: the server reports a real `total`, which is what
        // `StarjackTable`'s `TablePagination` needs. `page` is zero-based, the
        // same convention MUI uses, so it passes straight through.
        getAssistantChats: builder.query<
            ChatListPage,
            { page: number; limit: number }
        >({
            query: ({ page, limit }) => ({
                url: 'chats',
                params: { page, limit },
            }),
            providesTags: ['Chats'],
        }),
        // Fetched only for a `chatId` the caller deliberately selected — the
        // server logs every successful read as this cockpit user viewing this
        // chat's PII (spec §5.4), so this must never fire on render or hover.
        // Deliberately NOT tagged `'Chats'` (or any tag a future mutation
        // might invalidate): the audit log line is written on every fetch,
        // so an open transcript dialog silently re-fetching on some unrelated
        // chat-list mutation would write a second, unrequested read of a
        // real customer's PII under this user's name. Do not re-add a tag
        // here without re-reading this comment.
        getAssistantChat: builder.query<ChatDetail, string>({
            query: (chatId) => `chats/${encodeURIComponent(chatId)}`,
        }),
        // The operator is never sent: the server takes it from the token.
        getProductText: builder.query<ProductTextPayload, string>({
            query: (productId) => `products/${encodeURIComponent(productId)}`,
            providesTags: (_result, _error, productId) => [
                { type: 'ProductTexts', id: productId },
            ],
        }),
        putProductText: builder.mutation<
            ProductTextPayload,
            { productId: string; text: string }
        >({
            query: ({ productId, text }) => ({
                url: `products/${encodeURIComponent(productId)}`,
                method: 'PUT',
                body: { text },
            }),
            invalidatesTags: (_result, _error, { productId }) => [
                { type: 'ProductTexts', id: productId },
            ],
        }),
    }),
});

export const {
    useGetAssistantHealthQuery,
    useGetAssistantConfigQuery,
    usePutAssistantConfigMutation,
    useGetAssistantPagesQuery,
    usePutAssistantPageMutation,
    useGetAssistantQuestionsQuery,
    useCreateAssistantQuestionMutation,
    useUpdateAssistantQuestionMutation,
    useDeleteAssistantQuestionMutation,
    useGetAssistantChatsQuery,
    useGetAssistantChatQuery,
    useGetProductTextQuery,
    usePutProductTextMutation,
} = assistantApi;
